// Firebase configuration for RVINCENTE PWA
import { initializeApp } from 'firebase/app';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Cloud Messaging - lazy loaded to prevent crashes in WebViews
let messaging = null;
let _getToken = null;
let _onMessage = null;

// Check if the browser supports notifications
const isNotificationSupported = () => {
  return typeof Notification !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;
};

// Lazy initialize messaging only when needed and supported
const initMessaging = () => {
  if (messaging) return messaging;
  if (!isNotificationSupported()) return null;
  // If user has explicitly denied notifications, don't even try to init
  // messaging — saves a noisy "Service messaging is not available" error
  // in the console for the very common "denied" case.
  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
      return null;
    }
  } catch { /* ignore */ }
  try {
    const { getMessaging, getToken, onMessage } = require('firebase/messaging');
    messaging = getMessaging(app);
    _getToken = getToken;
    _onMessage = onMessage;
    return messaging;
  } catch (error) {
    // Don't pollute the console with a stack trace — this is expected on
    // incognito mode, in-app WebViews without messaging support, and on
    // some Safari versions. Log only at debug level.
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[Firebase] Messaging unavailable:', error?.message || error);
    }
    return null;
  }
};

// Request notification permission and get FCM token
export const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) {
    console.log('Push notifications not supported');
    return null;
  }

  try {
    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      console.log('Notification permission granted');
      
      const msg = initMessaging();
      if (!msg || !_getToken) return null;

      // Register service worker and force update
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        updateViaCache: 'none'
      });
      
      // Force check for SW updates
      try { await registration.update(); } catch (e) { /* ignore */ }
      
      // Wait for SW to be active
      if (registration.installing || registration.waiting) {
        await new Promise(resolve => {
          const sw = registration.installing || registration.waiting;
          sw.addEventListener('statechange', () => {
            if (sw.state === 'activated') resolve();
          });
          // Timeout after 5s
          setTimeout(resolve, 5000);
        });
      }

      console.log('[SW] Active:', registration.active?.state);

      // Get FCM token
      const token = await _getToken(msg, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration
      });
      
      if (token) {
        console.log('FCM Token obtained:', token.substring(0, 15) + '...');
        return token;
      } else {
        console.log('No FCM token available');
        return null;
      }
    } else {
      console.log('Notification permission denied');
      return null;
    }
  } catch (error) {
    console.error('Error getting notification permission:', error);
    return null;
  }
};

// Listen for foreground messages
export const onForegroundMessage = (callback) => {
  try {
    const msg = initMessaging();
    if (!msg || !_onMessage) {
      console.log('Messaging not initialized');
      return () => {};
    }
    
    return _onMessage(msg, (payload) => {
      console.log('Foreground message received:', payload);
      callback(payload);
    });
  } catch (error) {
    console.warn('onForegroundMessage setup failed:', error.message);
    return () => {};
  }
};

// Check if notifications are enabled
export const areNotificationsEnabled = () => {
  if (!isNotificationSupported()) return false;
  return Notification.permission === 'granted';
};

// Get current permission status
export const getNotificationPermissionStatus = () => {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission;
};

export { app, messaging, isNotificationSupported };
